import React, { useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Chip, Stack, ToggleButtonGroup, ToggleButton,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Divider, Button, Tooltip, Checkbox, Snackbar,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import LoadingView from '../components/LoadingView';
import { STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import {
  useToukiTable, GET_TOUKI_PROPERTY_DETAIL_URL, GET_TOUKI_DOCUMENT_PDF_URL,
  SET_TOUKI_WATCH_TARGET_URL,
} from '../hooks/useToukiTable';

type PropertyTab = '' | '土地' | '建物';

const pdfUrl = (documentId: number) => `${GET_TOUKI_DOCUMENT_PDF_URL}?document_id=${documentId}`;

// クローリング (申請用総合ソフト) の生存確認ステータス表示定義。
// 未知のステータス値はそのままラベル表示にフォールバックする。
const CHECK_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  alive: { label: '有効', color: '#2e7d32', bg: '#e8f5e9' },
  closed: { label: '閉鎖', color: '#c62828', bg: '#ffebee' },
  pending: { label: '事件受付中', color: '#6a1b9a', bg: '#f3e5f5' },
  error: { label: 'エラー', color: '#e65100', bg: '#fff3e0' },
};

const checkStatusView = (status: string) =>
  CHECK_STATUS_MAP[status] || { label: status, color: blueGrey[600], bg: '#eceff1' };

// timestamptz の ::text 表現 (YYYY-MM-DD HH:MM:SS...) を分単位まで整形する。
const formatCheckedAt = (v: any) => (v ? String(v).slice(0, 16) : '');

export default function ToukiManagement() {
  const {
    data, totalCount, loading, page, pageSize, filters,
    setPage, setPageSize, updateFilters, handleSort, setRowWatched, removeRow, insertRow,
  } = useToukiTable();

  const { user } = useAuth();
  const notify = useNotification();

  const [detailId, setDetailId] = useState<number | null>(null);
  // 原本PDFは画面内モーダルでプレビューする (新規タブで開かない)
  const [pdfDocId, setPdfDocId] = useState<number | null>(null);
  // 「対象のみ」表示中に外した行の Undo 情報 (復元用に行と元の位置を保持)。
  const [undoTarget, setUndoTarget] = useState<{ row: any; index: number } | null>(null);

  const markWatch = useCallback((propertyId: number, watched: boolean) =>
    axios.post(SET_TOUKI_WATCH_TARGET_URL, {
      property_id: propertyId, watched, marked_by: user?.email || '',
    }), [user]);

  // 対象マークのトグル。
  // 通常表示: 楽観的にチェック状態のみ反映 (行は残す)。
  // 「対象のみ」表示で外す場合: 行はフィルタに一致しなくなるため即除去し、Undo を提示する。
  const handleToggleWatch = useCallback(async (row: any, next: boolean) => {
    if (!next && filters.watched_only) {
      const index = data.findIndex((r: any) => r.id === row.id);
      removeRow(row.id);
      setUndoTarget({ row: { ...row, is_watched: true }, index });
      try {
        await markWatch(row.id, false);
      } catch (e) {
        insertRow({ ...row, is_watched: true }, index);
        setUndoTarget(null);
        notify.error('対象マークの更新に失敗しました');
      }
      return;
    }
    setRowWatched(row.id, next);
    try {
      await markWatch(row.id, next);
    } catch (e) {
      setRowWatched(row.id, !next);
      notify.error('対象マークの更新に失敗しました');
    }
  }, [filters.watched_only, data, removeRow, insertRow, setRowWatched, markWatch, notify]);

  // Undo。除去した行を元の位置へ戻し、再度マークする。
  const handleUndoRemove = useCallback(async () => {
    if (!undoTarget) return;
    const { row, index } = undoTarget;
    insertRow(row, index);
    setUndoTarget(null);
    try {
      await markWatch(row.id, true);
    } catch (e) {
      removeRow(row.id);
      notify.error('元に戻せませんでした');
    }
  }, [undoTarget, insertRow, removeRow, markWatch, notify]);

  const currentTab: PropertyTab = (filters.property_type as PropertyTab) || '';

  const handleTabChange = (_e: React.MouseEvent<HTMLElement>, v: PropertyTab | null) => {
    if (v === null) return;
    updateFilters({ property_type: v });
  };

  // 検索語の確定時のみ filters を更新 (入力中はテーブルを再描画しない)
  const handleSearch = useCallback((word: string) => {
    updateFilters({ search_word: word.trim() });
  }, [updateFilters]);

  const columns = useMemo<Column[]>(() => [
    {
      field: 'location', headerName: '対象 / 所在 / 地番', width: 360, sticky: true,
      renderCell: (row) => {
        const isLand = row.property_type === '土地';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Tooltip title={row.is_watched ? '対象を解除' : '法務局 事件確認の対象にする'}>
              <Checkbox
                size="small"
                checked={!!row.is_watched}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleToggleWatch(row, e.target.checked)}
                icon={<Icons.CheckBoxOutlineBlank sx={{ fontSize: 20 }} />}
                checkedIcon={<Icons.CheckBox sx={{ fontSize: 20, color: '#d32f2f' }} />}
                sx={{ p: 0.25, flexShrink: 0 }}
              />
            </Tooltip>
            <Box sx={{
              width: 26, height: 26, borderRadius: 1, flexShrink: 0,
              bgcolor: isLand ? '#e8f5e9' : '#e3f2fd',
              color: isLand ? '#2e7d32' : '#1976d2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isLand ? <Icons.Landscape sx={{ fontSize: 16 }} /> : <Icons.HomeWork sx={{ fontSize: 16 }} />}
            </Box>
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', minWidth: 0, cursor: 'pointer',
                '.MuiTableRow-root:hover &': { color: '#1976d2' },
              }}
              onClick={() => setDetailId(row.id)}
            >
              <Typography variant="body2" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {row.location || row.property_key || '-'}
              </Typography>
              <Typography variant="caption" color="text.disabled" noWrap sx={{ lineHeight: 1.3 }}>
                {row.chiban ? `地番 ${row.chiban}` : row.kaoku_no ? `家屋番号 ${row.kaoku_no}` : (row.realestate_no ? `不動産番号 ${row.realestate_no}` : '')}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'current_owner_name', headerName: '現所有者', width: 240,
      renderCell: (row) => {
        const name: string = row.current_owner_name || '';
        if (!name) return <Typography variant="body2" color="text.disabled">-</Typography>;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, py: 0.5 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              {row.current_owner_is_corp
                ? <Icons.Business sx={{ fontSize: 15, color: '#1976d2', flexShrink: 0 }} />
                : <Icons.Person sx={{ fontSize: 15, color: blueGrey[500], flexShrink: 0 }} />}
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{name}</Typography>
              {row.current_owner_share && (
                <Chip label={`持分 ${row.current_owner_share}`} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f1f5f9', flexShrink: 0 }} />
              )}
            </Box>
            {row.current_owner_address && (
              <Typography variant="caption" color="text.disabled" noWrap>{row.current_owner_address}</Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'detail', headerName: '物件属性', width: 220,
      renderCell: (row) => (
        <Stack spacing={0.3} sx={{ py: 0.5 }}>
          {row.property_type === '土地' ? (
            <Typography variant="body2" noWrap>
              {row.land_category || '-'}{row.land_area != null ? ` / ${row.land_area} ㎡` : ''}
            </Typography>
          ) : (
            <Typography variant="body2" noWrap>{row.building_type || '-'}{row.building_structure ? ` / ${row.building_structure}` : ''}</Typography>
          )}
          {row.has_lien && (
            <Chip icon={<Icons.AccountBalance sx={{ fontSize: 13 }} />} label="担保権あり" size="small"
              sx={{ height: 18, fontSize: '0.65rem', width: 'fit-content', bgcolor: '#fff3e0', color: '#e65100' }} />
          )}
        </Stack>
      ),
    },
    {
      field: 'last_checked_at', headerName: '登記確認', width: 130, align: 'center',
      renderCell: (row) => {
        if (!row.last_check_status) {
          return <Typography variant="caption" color="text.disabled">未確認</Typography>;
        }
        const s = checkStatusView(row.last_check_status);
        return (
          <Stack spacing={0.4} alignItems="center" sx={{ py: 0.5 }}>
            <Chip label={s.label} size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: s.color, bgcolor: s.bg }} />
            <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1 }}>
              {formatCheckedAt(row.last_checked_at)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'doc_count', headerName: '書類', width: 110, align: 'center',
      renderCell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <Chip icon={<Icons.Description sx={{ fontSize: 13 }} />} label={row.doc_count} size="small"
            sx={{ height: 20, bgcolor: '#eceff1' }} />
        </Stack>
      ),
    },
    {
      field: 'pdf', headerName: '原本', width: 90, align: 'center',
      renderCell: (row) => (
        row.primary_document_id ? (
          <Tooltip title="原本PDFをプレビュー">
            <IconButton size="small" onClick={() => setPdfDocId(row.primary_document_id)}>
              <Icons.PictureAsPdf sx={{ color: '#c62828' }} />
            </IconButton>
          </Tooltip>
        ) : <Typography variant="caption" color="text.disabled">-</Typography>
      ),
    },
  ], [handleToggleWatch]);

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: blueGrey[500], display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icons.FactCheck /> 登記簿謄本 管理リスト
        </Typography>
        <Typography variant="caption" color="text.secondary">
          法務局取得の登記事項証明書PDFの取込結果 (物件・現所有者・権利情報) を確認します
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={currentTab} exclusive onChange={handleTabChange}
          sx={{
            bgcolor: '#f5f5f7', borderRadius: 2, p: 0.3, border: '1px solid', borderColor: 'divider', gap: 0.5,
            '& .MuiToggleButton-root': {
              border: 'none', borderRadius: '6px !important', px: 1.5, py: 0.4, textTransform: 'none',
              color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem', gap: 0.75,
              '&.Mui-selected': { bgcolor: '#607d8b', color: '#fff' },
            },
          }}
        >
          <ToggleButton value=""><Icons.List sx={{ fontSize: 16 }} />全て</ToggleButton>
          <ToggleButton value="土地"><Icons.Landscape sx={{ fontSize: 16 }} />土地</ToggleButton>
          <ToggleButton value="建物"><Icons.HomeWork sx={{ fontSize: 16 }} />建物</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={<Icons.CheckBox sx={{ fontSize: 15 }} />}
            label="対象のみ" size="small" clickable
            color={filters.watched_only ? 'error' : 'default'}
            variant={filters.watched_only ? 'filled' : 'outlined'}
            onClick={() => updateFilters({ watched_only: !filters.watched_only })}
          />
          <Chip
            label="法人所有のみ" size="small" clickable
            color={filters.owner_is_corp ? 'primary' : 'default'}
            variant={filters.owner_is_corp ? 'filled' : 'outlined'}
            onClick={() => updateFilters({ owner_is_corp: !filters.owner_is_corp })}
          />
          <Chip
            label="担保権ありのみ" size="small" clickable
            color={filters.has_lien ? 'warning' : 'default'}
            variant={filters.has_lien ? 'filled' : 'outlined'}
            onClick={() => updateFilters({ has_lien: !filters.has_lien })}
          />
          <Chip
            icon={<Icons.Gavel sx={{ fontSize: 14 }} />}
            label="事件受付のみ" size="small" clickable
            color={filters.check_status === 'pending' ? 'secondary' : 'default'}
            variant={filters.check_status === 'pending' ? 'filled' : 'outlined'}
            onClick={() => updateFilters({ check_status: filters.check_status === 'pending' ? '' : 'pending' })}
          />
          <SearchBox onSearch={handleSearch} />
        </Stack>
      </Box>

      <Box sx={TABLE_FILL_SX}>
        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <StandardDataTable
            rows={data}
            loading={loading}
            loadingMessage="登記データを取得中"
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            columns={columns}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSortChange={handleSort}
          />
        </Box>
      </Box>

      <ToukiDetailDialog propertyId={detailId} onClose={() => setDetailId(null)} onOpenPdf={setPdfDocId} />
      <PdfPreviewDialog documentId={pdfDocId} onClose={() => setPdfDocId(null)} />

      {/* 「対象のみ」表示で外した行の Undo (誤操作の救済) */}
      <Snackbar
        open={undoTarget != null}
        autoHideDuration={6000}
        onClose={(_e, reason) => { if (reason !== 'clickaway') setUndoTarget(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="対象から外しました"
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button size="small" onClick={handleUndoRemove} sx={{ color: '#90caf9', fontWeight: 700 }}>
              元に戻す
            </Button>
            <IconButton size="small" onClick={() => setUndoTarget(null)} sx={{ color: '#fff' }}>
              <Icons.Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        }
      />
    </Box>
  );
}

/** 検索ボックス。入力状態をローカルに閉じ込め、確定時(Enter/ボタン)のみ onSearch を呼ぶ。
 *  親(テーブル)を入力のたびに再描画させないため React.memo で分離する。 */
const SearchBox = React.memo(function SearchBox({ onSearch }: { onSearch: (word: string) => void }) {
  const [value, setValue] = useState('');
  return (
    <TextField
      size="small" placeholder="所在・地番・所有者で検索 (Enterで実行)"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSearch(value); }}
      sx={{
        width: 300, bgcolor: '#fff',
        '& .MuiOutlinedInput-root': { height: 34, pr: 0.5 },
        '& .MuiOutlinedInput-input': { py: 0, fontSize: '0.8125rem' },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start"><Icons.Search sx={{ fontSize: 18 }} /></InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <Tooltip title="クリア">
              <IconButton
                size="small"
                onClick={() => { setValue(''); onSearch(''); }}
                edge="end"
              >
                <Icons.Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
});

/** 物件詳細ダイアログ (物件 + 表題部 + 権利部 + 原本PDF)。 */
function ToukiDetailDialog({ propertyId, onClose, onOpenPdf }: { propertyId: number | null; onClose: () => void; onOpenPdf: (documentId: number) => void }) {
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    setDetail(null);
    try {
      const res = await axios.post(GET_TOUKI_PROPERTY_DETAIL_URL, { property_id: id });
      if (res.data.status === 'success') setDetail(res.data.data);
    } catch (e) {
      console.error('Error fetching touki detail:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (propertyId != null) load(propertyId);
  }, [propertyId, load]);

  const open = propertyId != null;
  const p = detail?.property;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f1f5f9', py: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Icons.Description sx={{ color: blueGrey[500], fontSize: 18 }} />
          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
            {p ? (p.location || p.property_key) : '登記詳細'}
            {p?.chiban ? `　地番 ${p.chiban}` : p?.kaoku_no ? `　家屋番号 ${p.kaoku_no}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Icons.Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LoadingView message="詳細を取得中" height="40vh" size={50} />}
        {!loading && detail && (
          <Stack spacing={2.5}>
            {/* 物件サマリー */}
            <Box>
              <SectionLabel icon={<Icons.HomeWork sx={{ fontSize: 16 }} />} text="物件・現所有者" />
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                <KV k="種別" v={p.property_type} />
                <KV k="不動産番号" v={p.realestate_no} />
                {p.property_type === '土地'
                  ? <><KV k="地目" v={p.land_category} /><KV k="地積" v={p.land_area != null ? `${p.land_area} ㎡` : null} /></>
                  : <><KV k="種類" v={p.building_type} /><KV k="構造" v={p.building_structure} /></>}
                <KV k="現所有者" v={p.current_owner_name} />
                <KV k="所有者住所" v={p.current_owner_address} />
                {p.current_owner_share && <KV k="持分" v={p.current_owner_share} />}
                <KV k="担保権" v={p.has_lien ? 'あり' : 'なし'} />
              </Stack>
            </Box>

            {/* 書類 (原本PDF) */}
            <Box>
              <SectionLabel icon={<Icons.PictureAsPdf sx={{ fontSize: 16 }} />} text={`書類 (${detail.documents.length})`} />
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {detail.documents.map((d: any) => (
                  <Button key={d.id} size="small" variant="outlined"
                    startIcon={<Icons.PictureAsPdf sx={{ color: '#c62828' }} />}
                    onClick={() => onOpenPdf(d.id)} sx={{ textTransform: 'none' }}>
                    {d.doc_kind}{d.is_image ? '(画像)' : ''}
                  </Button>
                ))}
              </Stack>
            </Box>

            {/* 登記確認履歴 (申請用総合ソフト クローリングの生存確認結果、新しい順) */}
            {(detail.check_logs?.length ?? 0) > 0 && (
              <Box>
                <SectionLabel icon={<Icons.FactCheck sx={{ fontSize: 16 }} />} text={`登記確認履歴 (${detail.check_logs.length})`} />
                <MiniTable
                  head={['確認日時', '結果', '実行ID', '詳細']}
                  rows={detail.check_logs.map((l: any) => [
                    formatCheckedAt(l.checked_at),
                    checkStatusView(l.check_status).label,
                    l.run_id,
                    l.check_detail,
                  ])}
                  wrapLast
                />
              </Box>
            )}

            {/* 表題部 (所在・地図番号・筆界特定・調製は専用項目、地番/地目/地積の履歴は明細テーブル) */}
            {detail.hyodai.length > 0 && (() => {
              // 表題部 先頭のメタ行(地図番号値・所在の履歴)を明細から分離する。
              // 登記簿様式上これらは①地番②地目③地積の表より前に来るため、最初の明細行までを先頭メタとして切り出す。
              const firstDetailIdx = detail.hyodai.findIndex((h: any) =>
                p.property_type === '土地'
                  ? (h.chiban || h.land_area != null)
                  : (h.building_type || h.floor_area_text));
              const leadMeta = firstDetailIdx > 0 ? detail.hyodai.slice(0, firstDetailIdx) : [];
              const detailRows = firstDetailIdx >= 0 ? detail.hyodai.slice(firstDetailIdx) : detail.hyodai;
              // 先頭メタ行のうち地図番号と一致しないもの = 所在の変遷。最新(最下行)が現在の所在。
              // 行政区画変更等の変更原因日付(cause_and_date)があれば括弧で併記し、段落ちで積み上げる。
              const shozaiEntries = leadMeta
                .map((h: any) => ({ place: h.land_category || h.building_type, date: h.cause_and_date }))
                .filter((e: any) => e.place && e.place !== p.map_no);
              const shozaiLines = (shozaiEntries.length
                ? shozaiEntries.map((e: any) =>
                    e.date ? `${e.place}（${String(e.date).replace(/\n/g, ' ')}）` : e.place)
                : [p.location].filter(Boolean));
              return (
                <Box>
                  <SectionLabel icon={<Icons.Description sx={{ fontSize: 16 }} />} text="表題部" />
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      <KV k="地図番号" v={p.map_no} />
                      <KV k="筆界特定" v={p.hikkai_tokutei} />
                      <KV k="調製" v={p.chosei_date} />
                    </Stack>
                    <KV k="所在" v={shozaiLines.join('\n') || null} wrap />
                  </Stack>
                  {detailRows.length > 0 && (
                    <MiniTable
                      head={p.property_type === '土地'
                        ? ['地番', '地目', '地積', '原因及びその日付']
                        : ['種類', '構造', '床面積', '原因及びその日付']}
                      rows={detailRows.map((h: any) => p.property_type === '土地'
                        ? [h.chiban, h.land_category, h.land_area, h.cause_and_date]
                        : [h.building_type, h.building_structure, h.floor_area_text, h.cause_and_date])}
                    />
                  )}
                </Box>
              );
            })()}

            {/* 権利部 */}
            {detail.rights.length > 0 && (
              <Box>
                <SectionLabel icon={<Icons.AccountBalance sx={{ fontSize: 16 }} />} text={`権利部 (${detail.rights.length})`} />
                <MiniTable
                  head={['区', '順位', '登記の目的', '受付', '権利者その他の事項']}
                  rows={detail.rights.map((r: any) => [
                    r.section,
                    `${r.rank_no || ''}${r.sub_rank ? ` ${r.sub_rank}` : ''}`,
                    r.purpose,
                    `${r.reception_date || ''} ${r.reception_no || ''}`.trim(),
                    r.rights_other_raw,
                  ])}
                  wrapLast
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** 原本PDFを画面内モーダルでプレビューするダイアログ (iframe表示)。 */
function PdfPreviewDialog({ documentId, onClose }: { documentId: number | null; onClose: () => void }) {
  const open = documentId != null;
  const src = documentId != null ? pdfUrl(documentId) : '';
  // GCSからのPDF取得には数百ms〜数秒のラグがあるため、iframeのonLoad完了まで
  // ローディングをオーバーレイ表示する。documentが変わるたびにtrueへ戻す。
  const [pdfLoading, setPdfLoading] = useState(true);
  React.useEffect(() => {
    if (documentId != null) setPdfLoading(true);
  }, [documentId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f1f5f9', py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Icons.PictureAsPdf sx={{ color: '#c62828', fontSize: 18 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>原本PDF プレビュー</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {src && (
            <Button size="small" onClick={() => window.open(src, '_blank', 'noopener')}
              sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.75rem' }}>
              新しいタブで開く
            </Button>
          )}
          <IconButton onClick={onClose} size="small"><Icons.Close sx={{ fontSize: 18 }} /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#525659', position: 'relative', overflow: 'hidden' }}>
        {src && (
          <iframe
            title="登記原本PDF"
            src={src}
            onLoad={() => setPdfLoading(false)}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
        {pdfLoading && (
          <LoadingView
            message="PDFを読み込み中"
            size={50}
            sx={{
              position: 'absolute', inset: 0,
              bgcolor: 'rgba(255, 255, 255, 0.94)',
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: blueGrey[600] }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{text}</Typography>
      <Divider sx={{ flexGrow: 1, ml: 1 }} />
    </Box>
  );
}

function KV({ k, v, wrap }: { k: string; v: any; wrap?: boolean }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" color="text.disabled">{k}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: wrap ? 'pre-line' : undefined }}>{v ?? '-'}</Typography>
    </Box>
  );
}

function MiniTable({ head, rows, wrapLast }: { head: string[]; rows: any[][]; wrapLast?: boolean }) {
  return (
    <Table size="small" sx={{ mt: 1, '& td, & th': { fontSize: '0.78rem', py: 0.5 } }}>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f8fafc' }}>
          {head.map((h, i) => <TableCell key={i} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, ri) => (
          <TableRow key={ri}>
            {r.map((c, ci) => (
              <TableCell key={ci}
                sx={ci === r.length - 1 && wrapLast
                  ? { whiteSpace: 'pre-wrap', verticalAlign: 'top', color: 'text.secondary' }
                  : { verticalAlign: 'top' }}>
                {c ?? '-'}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
